import { WS_CONFIG } from '../config/api';

// WebSocket Service for Real-time Communication
// Updated: 2026-05-03 - Vercel build cache refresh

export interface WebSocketMessage {
  type: string;
  brand_id?: string;
  client_id?: string;
  timestamp: string;
  data?: any;
  message?: string;
  conversation_id?: string;
  automation_id?: string;
}

export interface WebSocketEventHandler {
  (message: WebSocketMessage): void;
}

class WebSocketService {
  private ws: WebSocket | null = null;
  private eventHandlers: Map<string, WebSocketEventHandler[]> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = WS_CONFIG.RECONNECT_ATTEMPTS;
  private reconnectInterval = WS_CONFIG.RECONNECT_INTERVAL;
  private pingInterval: NodeJS.Timeout | null = null;
  private isConnected = false;
  private brandId: string | null = null;
  private clientId: string;
  private messageQueue: WebSocketMessage[] = [];

  constructor() {
    this.clientId = this.generateClientId();
  }

  private generateClientId(): string {
    return 'client_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
  }

  // Connect to WebSocket
  connect(brandId: string = 'demo_brand', token: string = 'dev_token'): Promise<boolean> {
    return new Promise((resolve, reject) => {
      try {
        this.brandId = brandId;
        const wsUrl = `${WS_CONFIG.BASE_URL}/api/v1/ws/connect?client_id=${this.clientId}&brand_id=${brandId}`;
        
        console.log('🔌 Connecting to WebSocket:', wsUrl);
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = (event) => {
          console.log('✅ WebSocket connected successfully to:', wsUrl);
          this.isConnected = true;
          this.reconnectAttempts = 0;
          this.startPing();
          this.processMessageQueue();
          
          // Send initial connection message
          this.send({
            type: 'client_connected',
            client_id: this.clientId,
            brand_id: brandId,
            timestamp: new Date().toISOString(),
            data: { userAgent: navigator.userAgent }
          });
          
          resolve(true);
        };

        this.ws.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            console.log('📨 WebSocket message received:', message.type, message);
            this.handleMessage(message);
          } catch (error) {
            console.error('❌ Failed to parse WebSocket message:', error);
          }
        };

        this.ws.onclose = (event) => {
          console.log('🔌 WebSocket connection closed:', event.code, event.reason);
          this.isConnected = false;
          this.stopPing();

          // Attempt to reconnect if not a normal closure
          if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            console.log(`🔄 Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
            setTimeout(() => {
              if (this.brandId) {
                this.connect(this.brandId, token);
              }
            }, this.reconnectInterval * this.reconnectAttempts);
          }
        };

        this.ws.onerror = (event) => {
          console.error('❌ WebSocket error:', event);
          const errorMessage = event instanceof ErrorEvent ? event.message : 'WebSocket connection failed';
          reject(new Error(errorMessage));
        };

        // Connection timeout
        setTimeout(() => {
          if (!this.isConnected) {
            console.error('⏰ WebSocket connection timeout');
            reject(new Error('WebSocket connection timeout'));
          }
        }, 10000);

      } catch (error) {
        console.error('❌ WebSocket connection error:', error);
        reject(error);
      }
    });
  }

  // Enhanced connect method that auto-connects
  async autoConnect(brandId: string = 'demo_brand'): Promise<void> {
    try {
      await this.connect(brandId);
      console.log('🎉 WebSocket auto-connection successful');
    } catch (error) {
      console.error('❌ WebSocket auto-connection failed:', error);
      // Don't throw, let the app continue working
    }
  }

  // Disconnect WebSocket
  disconnect() {
    if (this.ws) {
      this.ws.close(1000, 'Manual disconnect');
      this.ws = null;
    }
    this.isConnected = false;
    this.stopPing();
  }

  // Process queued messages when connection is restored
  private processMessageQueue(): void {
    console.log(`📦 Processing ${this.messageQueue.length} queued messages`);
    while (this.messageQueue.length > 0 && this.isConnected) {
      const message = this.messageQueue.shift();
      if (message) {
        this.send(message);
      }
    }
  }

  // Send message with queueing support
  send(message: any): boolean {
    const enrichedMessage = {
      ...message,
      client_id: this.clientId,
      timestamp: message.timestamp || new Date().toISOString()
    };

    if (this.ws && this.isConnected) {
      try {
        console.log('📤 Sending WebSocket message:', enrichedMessage.type);
        this.ws.send(JSON.stringify(enrichedMessage));
        return true;
      } catch (error) {
        console.error('❌ Failed to send WebSocket message:', error);
        return false;
      }
    } else {
      console.log('📦 Queueing message (not connected):', enrichedMessage.type);
      this.messageQueue.push(enrichedMessage);
      return false;
    }
  }

  // Enhanced send methods for specific functionality
  sendChatMessage(message: string, conversationId?: string, agentType = 'brand_copilot'): void {
    this.send({
      type: 'chat_message',
      data: {
        message,
        conversationId,
        agentType,
        userId: this.clientId
      }
    });
  }

  requestAnalyticsUpdate(): void {
    this.send({
      type: 'request_analytics_update',
      data: { source: 'dashboard' }
    });
  }

  triggerProposalAutomation(leadId: string, companyName: string): void {
    this.send({
      type: 'trigger_proposal_automation',
      data: {
        leadId,
        companyName,
        userId: this.clientId
      }
    });
  }

  notifyDocumentUpload(documentId: string, filename: string): void {
    this.send({
      type: 'document_upload_notification',
      data: {
        documentId,
        filename,
        userId: this.clientId
      }
    });
  }

  // Subscribe to event type
  on(eventType: string, handler: WebSocketEventHandler) {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, []);
    }
    this.eventHandlers.get(eventType)!.push(handler);
  }

  // Unsubscribe from event type
  off(eventType: string, handler?: WebSocketEventHandler) {
    if (!this.eventHandlers.has(eventType)) return;

    if (handler) {
      const handlers = this.eventHandlers.get(eventType)!;
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    } else {
      this.eventHandlers.delete(eventType);
    }
  }

  // Handle incoming message
  private handleMessage(message: WebSocketMessage) {
    console.log('WebSocket message received:', message);

    // Emit to specific event handlers
    const handlers = this.eventHandlers.get(message.type);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(message);
        } catch (error) {
          console.error('Error in WebSocket event handler:', error);
        }
      });
    }

    // Emit to global handlers
    const globalHandlers = this.eventHandlers.get('*');
    if (globalHandlers) {
      globalHandlers.forEach(handler => {
        try {
          handler(message);
        } catch (error) {
          console.error('Error in global WebSocket event handler:', error);
        }
      });
    }
  }

  // Start ping to keep connection alive
  private startPing() {
    this.pingInterval = setInterval(() => {
      if (this.isConnected) {
        this.send({ type: 'ping', timestamp: new Date().toISOString() });
      }
    }, WS_CONFIG.PING_INTERVAL);
  }

  // Stop ping
  private stopPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  // Get connection status
  getConnectionStatus(): {
    connected: boolean;
    readyState: number | null;
    reconnectAttempts: number;
    brandId: string | null;
  } {
    return {
      connected: this.isConnected,
      readyState: this.ws?.readyState || null,
      reconnectAttempts: this.reconnectAttempts,
      brandId: this.brandId
    };
  }
}

// Create singleton instance
export const webSocketService = new WebSocketService();

// Convenience functions for common events
export const subscribeToDocumentEvents = (handler: WebSocketEventHandler) => {
  webSocketService.on('document_upload_progress', handler);
  webSocketService.on('document_processing_started', handler);
  webSocketService.on('document_processing_completed', handler);
};

export const subscribeToChatEvents = (handler: WebSocketEventHandler) => {
  webSocketService.on('chat_message_received', handler);
  webSocketService.on('chat_message_sent', handler);
};

export const subscribeToProposalEvents = (handler: WebSocketEventHandler) => {
  webSocketService.on('proposal_automation_progress', handler);
  webSocketService.on('proposal_automation_completed', handler);
  webSocketService.on('proposal_automation_failed', handler);
};

export const subscribeToInsightEvents = (handler: WebSocketEventHandler) => {
  webSocketService.on('ai_insight_generated', handler);
};

export default webSocketService;